import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EnergyCertificateAvailability } from '../../../../../../@types/radio-types'
import { ContentStateInterface, ContentStateProperty, ScreenProperty } from '../../../../../../@types/types'
import EnergyCertificateAvailable from './EnergyCertificateAvailable'
import { ConstructionScreenValidationResult } from '../../../../../../@types/validation-types'
import { getEnergyCertificateLabels } from '../../../../../../services/domain/getEnergyCertificateLabels'
import { Notification, RadioButton } from 'is24-corecss'

export interface EnergyCertificateProps {
  screenId: string
  insertionData: ContentStateInterface
  validators: ConstructionScreenValidationResult
  onEnergyCertificateChange: (field: ContentStateProperty, value: string | number | boolean | undefined) => void
  doReset: (clearingUpOf: ScreenProperty[]) => void
}

const SCREEN_NAME = 'construction' as const

const ENERGY_CERTIFICATE_RESET_PROPERTIES: ScreenProperty[] = [
  { screen: SCREEN_NAME, property: 'buildingEnergyRatingType' },
  { screen: SCREEN_NAME, property: 'thermalCharacteristic' },
  { screen: SCREEN_NAME, property: 'efficiencyClass' },
  { screen: SCREEN_NAME, property: 'legalConstructionYear' },
] as const

const EnergyCertificate: React.FC<EnergyCertificateProps> = ({
  screenId,
  insertionData,
  validators,
  onEnergyCertificateChange,
  doReset,
}) => {
  const [selectedRadioButton, setSelectedRadioButton] = useState<EnergyCertificateAvailability | undefined>(
    insertionData.energyCertificateAvailability
  )
  const { t } = useTranslation()

  // Sync with props when they change (important for external updates)
  useEffect(() => {
    setSelectedRadioButton(insertionData.energyCertificateAvailability)
  }, [insertionData.energyCertificateAvailability])

  const handleRadioChange = (value: string) => {
    const newValue = value as EnergyCertificateAvailability
    setSelectedRadioButton(newValue)
    onEnergyCertificateChange('energyCertificateAvailability', newValue)

    if (newValue !== EnergyCertificateAvailability.AVAILABLE) {
      doReset(ENERGY_CERTIFICATE_RESET_PROPERTIES)
    }
  }

  return (
    <div className='grid-item one-whole'>
      <RadioButton.Root name='energy-certificate-label' value={selectedRadioButton} onValueChange={handleRadioChange}>
        <RadioButton.Legend className='visually-hidden'>
          {t('wizard.screen.construction.energyCertificate')}
        </RadioButton.Legend>
        {getEnergyCertificateLabels(t).map((key) => {
          return (
            <React.Fragment key={key.radioId}>
              <RadioButton.Item id={key.radioId} value={key.radioId}>
                <RadioButton.Input />
                <RadioButton.Label>{key.label}</RadioButton.Label>
              </RadioButton.Item>

              {key.radioId === EnergyCertificateAvailability.NOT_AVAILABLE_YET &&
                selectedRadioButton === EnergyCertificateAvailability.NOT_AVAILABLE_YET && (
                  <Notification.Root className='margin-vertical-s' type={'info'} variant={'flat'}>
                    <Notification.Icon />
                    <Notification.Content>
                      <Notification.Title>{t('statusMessage.messages.noEnergyCertificateInfo')}</Notification.Title>
                      <Notification.Message>
                        {t('statusMessage.messages.noEnergyCertificateInfoSecond')}
                      </Notification.Message>
                    </Notification.Content>
                  </Notification.Root>
                )}

              {key.radioId === EnergyCertificateAvailability.AVAILABLE &&
                selectedRadioButton === EnergyCertificateAvailability.AVAILABLE && (
                  <EnergyCertificateAvailable
                    screenId={screenId}
                    onEnergyCertificateChange={onEnergyCertificateChange}
                    insertionData={insertionData}
                    validators={validators}
                    doReset={doReset}
                  />
                )}
            </React.Fragment>
          )
        })}
      </RadioButton.Root>
    </div>
  )
}

export default EnergyCertificate
